/**
 * Social sign-in providers.
 *
 * Listed here rather than in the console so the Flutter app offers the same
 * three, in the same order, under the same names - a technician who signed up
 * with Microsoft on the web must not be shown only Google on the phone.
 */

export const OAUTH_PROVIDERS = [
  {
    id: "google",
    label: "Google",
    /** Default scopes are enough; listed for symmetry with the other two. */
    scopes: "email profile",
  },
  {
    /**
     * Microsoft Entra ID - what a work Outlook/Microsoft 365 account signs in
     * with. Supabase calls it `azure`; users call it Microsoft, so the label and
     * the id deliberately differ.
     */
    id: "azure",
    label: "Microsoft",
    /** Entra returns no email address unless it is asked for explicitly. */
    scopes: "openid email profile",
  },
  {
    id: "apple",
    label: "Apple",
    /**
     * Apple sends the name **once**, on the very first authorisation, and never
     * again. If the profile is not captured then it cannot be recovered without
     * the user revoking access in their Apple ID settings and starting over.
     */
    scopes: "email name",
  },
] as const;

export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number]["id"];

export function oauthProvider(id: OAuthProvider) {
  return OAUTH_PROVIDERS.find((provider) => provider.id === id)!;
}

/* ────────────────────────── account deletion ────────────────────────── */

/**
 * What somebody types to confirm `DELETE /auth/me`.
 *
 * Here rather than in either client because the console and the phone must ask
 * for the same word - a technician who has been told "type DELETE" by a
 * colleague on the web should not find the phone wanting "delete" or their own
 * email address. The parity rule in docs/conventions.md, applied to a string.
 *
 * Compared case-sensitively on purpose. The friction *is* the feature: this is
 * the one irreversible action in the product, and an accidental one cannot be
 * undone by anybody, including us.
 */
export const DELETE_ACCOUNT_PHRASE = "DELETE";

/**
 * What is actually destroyed, and what survives - the sentences both clients
 * show above that field, and the ones the privacy notice has to match.
 *
 * Written out rather than summarised as "your data will be deleted", because
 * that would be a lie: `visits.user_id` and `assessments.user_id` are
 * `on delete restrict` precisely so a safety record keeps naming who signed it.
 * Apple's 5.1.1(v) and GDPR Art. 17(3)(b) both allow retention that another
 * obligation requires - on the condition that it is disclosed. This is the
 * disclosure.
 *
 * Keep in step with `public.delete_my_account()`. If that function stops doing
 * one of these, or starts doing something else, this list is wrong and the
 * product is making a promise it does not keep.
 */
export const DELETE_ACCOUNT_EFFECTS = {
  removed: [
    "Your sign-in: password, any connected Google, Microsoft or Apple account, and two-factor.",
    "Your name, email address, phone number and picture.",
    "Your access to every site. You will be signed out on every device.",
  ],
  retained: [
    "Completed visits and safety assessments, including photographs and video. These are your employer's compliance records and are kept, no longer showing your name.",
    "Scheduled work is handed back to your team to reassign; anything you had open is closed as abandoned.",
  ],
} as const;
